import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { searchServiceLanes } from '../../api/endpoints/admin';
import { DataTable } from '../../components/ui/DataTable';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { StatusPill } from '../../components/ui/StatusPill';
import { SearchableSelect, type SearchableOption } from '../../components/ui/SearchableSelect';
import { kycStatusColor } from '../../components/domain/statusColor';
import { useTruckCatalog } from '../../hooks/useTruckCatalog';
import { INDIAN_STATES, formatStateWithCapital } from '../../config/indianStates';
import type { KycStatus } from '../../api/types';
import { referenceQueryPolicy } from '../../api/queryPolicies';
import styles from './ServiceLanesView.module.css';

/** Pre-built options for the searchable select. */
const STATE_OPTIONS: SearchableOption[] = INDIAN_STATES.map((s) => ({
  id: s.id,
  label: s.name,
  subtitle: s.capital,
}));

export function ServiceLanesView() {
  const { t } = useTranslation();
  const [fromState, setFromState] = useState('');
  const [toState, setToState] = useState('');
  const [truckType, setTruckType] = useState('');
  // Catalog types only, never "Other" (#249 Contract section 5).
  const truckCatalog = useTruckCatalog();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'service-lanes', 'search', fromState, toState, truckType],
    queryFn: () =>
      searchServiceLanes({
        fromState: fromState || undefined,
        toState: toState || undefined,
        truckType: truckType || undefined,
      }),
    ...referenceQueryPolicy,
  });

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <SearchableSelect
          options={STATE_OPTIONS}
          value={fromState}
          onChange={setFromState}
          placeholder={t('serviceLanes.serviceLanesView.allOriginStates')}
          allowEmpty
        />

        <SearchableSelect
          options={STATE_OPTIONS}
          value={toState}
          onChange={setToState}
          placeholder={t('serviceLanes.serviceLanesView.allDestinationStates')}
          allowEmpty
        />

        <select
          className={styles.select}
          value={truckType}
          onChange={(e) => setTruckType(e.target.value)}
        >
          <option value="">{t('serviceLanes.serviceLanesView.allTruckTypes')}</option>
          {truckCatalog.trucks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        <RefreshButton
          queryKeys={[['admin', 'service-lanes', 'search', fromState, toState, truckType]]}
        />
      </div>



      <DataTable
        loading={isLoading}
        error={isError ? "We couldn’t load service lanes" : undefined}
        onRetry={() => refetch()}
        keyFn={(l) => `${l.fleetManagerId}-${l.fromState}-${l.toState}`}
        rows={data ?? []}
        empty="No service lanes found matching the filters"
        columns={[
          {
            key: 'fleetManager',
            header: 'Partner',
            render: (l) => (
              <Link to={`/users/${l.fleetManagerId}`} className={styles.link}>
                {l.companyName || 'Unknown partner'}
              </Link>
            ),
          },
          {
            key: 'fleetSize',
            header: 'Fleet Size',
            render: (l) => l.fleetSize,
          },
          {
            key: 'status',
            header: 'KYC Status',
            render: (l) => {
              if (!l.verificationStatus) return '—';
              const colors = kycStatusColor(l.verificationStatus as KycStatus);
              return (
                <StatusPill bg={colors.bg} text={colors.text} label={l.verificationStatus} />
              );
            },
          },
          {
            key: 'route',
            header: 'Route',
            render: (l) => (
              <span>
                {formatStateWithCapital(l.fromState)} → {formatStateWithCapital(l.toState)}
              </span>
            ),
          },
          {
            key: 'truckTypes',
            header: 'Truck Types Offered',
            render: (l) => (
              <div className={styles.tagList}>
                {l.truckTypes.map((t) => (
                  <span key={t} className={styles.tag}>
                    {t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                ))}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
